<template>
  <hel-module title="生产订单物料齐套率（近6个月）" :class="scope.clsPrefix" v-loading="loading">
    <div class="chart-container">
      <div id="materialKitRateChart" class="chart-body"></div>
      <div class="chart-subtitle">
        月份（{{ startMonth }} ~ {{ endMonth }}）
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from '../common/module/index.vue'
import * as echarts from 'echarts'
import { getMaterialKitRate } from '@/api/procurement'

export default {
  name: 'MaterialKitRate',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-material-kit-rate'
      },
      months: [],
      startMonth: '--',
      endMonth: '--',
      kitRates: [],
      chartInstance: null
    }
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getMaterialKitRate()
        if (!res || res.code !== 200 || !Array.isArray(res.data) || !res.data.length) {
          this.months = []
          this.kitRates = []
          this.startMonth = '--'
          this.endMonth = '--'
          return
        }
        const data = res.data
        this.months = data.map((i) => {
          const parts = i.statMonth ? i.statMonth.split('-') : []
          return parts.length === 2 ? String(Number(parts[1])) + '月' : i.statMonth
        })
        this.startMonth = data[0].statMonth
        this.endMonth = data[data.length - 1].statMonth
        this.kitRates = data.map((i) => Number(i.kitRate) || 0)

        this.$nextTick(() => {
          this.drawChart()
        })
      } catch (error) {
        console.error('获取生产订单物料齐套率数据失败:', error)
        this.months = []
        this.kitRates = []
        this.startMonth = '--'
        this.endMonth = '--'
      } finally {
        this.loading = false
      }
    },
    drawChart() {
      const chart = echarts.init(
        document.getElementById('materialKitRateChart')
      )
      // 动态计算 Y 轴范围：min 不小于 0，max 不超过 100，间隔按范围自适应
      const dataMin = this.kitRates.length ? Math.min(...this.kitRates) : 0
      const dataMax = this.kitRates.length ? Math.max(...this.kitRates) : 100
      let yMin = dataMin >= 80 ? 80 : Math.floor(dataMin / 10) * 10 - 10
      if (yMin < 0) yMin = 0
      let yMax = Math.ceil(dataMax / 10) * 10 + 10
      if (yMax > 100) yMax = 100
      if (yMax <= yMin) yMax = Math.min(100, yMin + 10)
      const interval = yMax - yMin >= 40 ? 10 : 5
      const option = {
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: {
            type: 'cross'
          },
          formatter: (params) => {
            let html = `${params[0].axisValue}<br/>`
            params.forEach((p) => {
              html += `${p.marker}${p.seriesName}: ${p.value}%<br/>`
            })
            return html
          }
        },
        grid: {
          top: 32,
          left: 10,
          right: 10,
          bottom: 10,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.months,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#999999',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          name: '齐套率（%）',
          nameTextStyle: {
            color: '#999999',
            fontSize: 12,
            padding: [0, 0, 0, 30]
          },
          min: yMin,
          max: yMax,
          interval: interval,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            lineStyle: {
              color: '#E8E8E8',
              type: 'solid'
            }
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12,
            formatter: '{value}%'
          }
        },
        series: [
          {
            name: '齐套率',
            type: 'line',
            symbol: 'circle',
            symbolSize: 10,
            itemStyle: {
              color: '#fff',
              borderColor: '#8BC48A',
              borderWidth: 2
            },
            lineStyle: { color: '#8BC48A', width: 2.5 },
            label: {
              show: true,
              position: 'top',
              color: '#8BC48A',
              fontSize: 12,
              formatter: '{c}%'
            },
            data: this.kitRates
          }
        ]
      }
      chart.setOption(option)
      this.chartInstance = chart
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartInstance && this.chartInstance.resize()
        })
        setTimeout(() => {
          this.chartInstance && this.chartInstance.resize()
        }, 200)
      })
    },
    handleResize() {
      if (this.chartInstance) {
        setTimeout(() => {
          this.chartInstance.resize()
        }, 200)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-material-kit-rate";
.#{$class-prefix} {
  .chart-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 10px 0;
    box-sizing: border-box;
  }
  .chart-subtitle {
    align-self: center;
    font-family: PingFangSC-Regular;
    font-size: 12px;
    color: #999999;
    font-weight: 400;
    margin-top: 4px;
  }
  .chart-body {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 260px;
  }
}
</style>
